import { ControlCodes } from '@bcube/shared/constants';
import { useQuery } from '@tanstack/react-query';
import { format, isValid, parse } from 'date-fns';
import { motion } from 'framer-motion';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { Link, useSearchParams } from 'react-router';
import { PriceTableStatus, priceTableApi } from '@/api/commercial/price-tables';
import { PriceTableTable } from '@/components/table/commercial/price-table-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { DateRangeFilterInput } from '@/components/ui/date-range-filter-input';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/auth-context';

type TableTypeFilter = 'Todos' | 'Normal' | 'Provisória';
type StatusFilter = 'Todos' | PriceTableStatus;

const ISO_DATE_FORMAT = 'yyyy-MM-dd';

function parseUrlDate(value: string | null): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, ISO_DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
}

function formatToIso(date: Date | undefined): string | undefined {
  return date && isValid(date) ? format(date, ISO_DATE_FORMAT) : undefined;
}

const TYPE_OPTIONS: { value: TableTypeFilter; label: string }[] = [
  { value: 'Todos', label: 'Todos' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Provisória', label: 'Provisória' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'Todos', label: 'Todos' },
  { value: PriceTableStatus.ACTIVE, label: 'Ativa' },
  { value: PriceTableStatus.PAUSED, label: 'Em Pausa' },
  { value: PriceTableStatus.DRAFT, label: 'Rascunho' },
  { value: PriceTableStatus.AWAITING_PUBLICATION, label: 'Aguardando Publicação' },
  { value: PriceTableStatus.INACTIVE, label: 'Inativa' },
  { value: PriceTableStatus.CANCELLED, label: 'Cancelada' },
];

export function PriceTableListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { restrictFeatureByProfile } = useAuth();

  const canCreate = restrictFeatureByProfile(ControlCodes.commercial_price_table_create);
  const canEdit = restrictFeatureByProfile(ControlCodes.commercial_price_table_edit);

  // Filter states from URL
  const [filterName, setFilterName] = useState(searchParams.get('name') || '');
  const [filterType, setFilterType] = useState<TableTypeFilter>(
    (searchParams.get('type') as TableTypeFilter) || 'Todos',
  );
  const [filterStatus, setFilterStatus] = useState<StatusFilter>(
    (searchParams.get('status') as StatusFilter) || 'Todos',
  );
  const [filterCreatedAt, setFilterCreatedAt] = useState<Date | undefined>(
    parseUrlDate(searchParams.get('createdAt')),
  );
  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>(
    parseUrlDate(searchParams.get('startDate')),
  );
  const [filterStartDateRange, setFilterStartDateRange] = useState<DateRange | undefined>(() => {
    const from = parseUrlDate(searchParams.get('startDateFrom'));
    const to = parseUrlDate(searchParams.get('startDateTo'));
    return from || to ? { from, to } : undefined;
  });

  const page = Number(searchParams.get('page')) || 1;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['price-tables', Object.fromEntries(searchParams)],
    queryFn: () => {
      const params: Record<string, string | number | boolean> = {
        useStatusOrder: true,
      };
      const name = searchParams.get('name');
      const status = searchParams.get('status');
      const pageParam = searchParams.get('page');
      const createdAt = searchParams.get('createdAt');
      const startDate = searchParams.get('startDate');
      const type = searchParams.get('type');
      const startDateFrom = searchParams.get('startDateFrom');
      const startDateTo = searchParams.get('startDateTo');

      if (name) params.search = name;
      if (status && status !== 'Todos') params.status = status;
      if (type && type !== 'Todos') params.isTemporary = type === 'Provisória';
      if (pageParam) params.page = Number(pageParam);
      if (createdAt) params.createdAt = createdAt;
      if (startDate) params.startDate = startDate;
      if (startDateFrom) params.startDateFrom = startDateFrom;
      if (startDateTo) params.startDateTo = startDateTo;

      return priceTableApi.findMany(params);
    },
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    if (filterName) params.set('name', filterName);
    if (filterType && filterType !== 'Todos') params.set('type', filterType);
    if (filterStatus && filterStatus !== 'Todos') params.set('status', filterStatus);

    const createdAtIso = formatToIso(filterCreatedAt);
    if (createdAtIso) params.set('createdAt', createdAtIso);

    const startDateIso = formatToIso(filterStartDate);
    if (startDateIso) params.set('startDate', startDateIso);

    const startDateFromIso = formatToIso(filterStartDateRange?.from);
    if (startDateFromIso) params.set('startDateFrom', startDateFromIso);

    const startDateToIso = formatToIso(filterStartDateRange?.to);
    if (startDateToIso) params.set('startDateTo', startDateToIso);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterType('Todos');
    setFilterStatus('Todos');
    setFilterCreatedAt(undefined);
    setFilterStartDate(undefined);
    setFilterStartDateRange(undefined);
    setSearchParams(new URLSearchParams());
  };

  const filteredData = data ? { ...data, docs: data.docs ?? [] } : undefined;

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar tabelas de preços</p>
        <Button onClick={() => refetch()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Tabela de Preços</h1>
          <p className="text-muted-foreground">Visualize e gerencie todas as tabelas de preços</p>
        </div>
        {canCreate && (
          <Button asChild className="bg-black text-white hover:bg-gray-800">
            <Link to="/commercial/price-table/form/create">
              <Plus className="h-4 w-4 mr-2" />
              Nova Tabela
            </Link>
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Preencha os filtros abaixo e clique em "Pesquisar"</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div>
                <Input
                  label="Nome da tabela"
                  placeholder="Nome da tabela"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700">Tipo de tabela</span>
                <Select
                  value={filterType}
                  onValueChange={(value) => setFilterType(value as TableTypeFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700">Status da tabela</span>
                <Select
                  value={filterStatus}
                  onValueChange={(value) => setFilterStatus(value as StatusFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DatePickerInput
                label="Data de cadastro"
                value={filterCreatedAt}
                onChange={setFilterCreatedAt}
              />
              <DatePickerInput
                label="Data inicial"
                value={filterStartDate}
                onChange={setFilterStartDate}
              />
              <DateRangeFilterInput
                label="Período inicial"
                value={filterStartDateRange}
                onChange={setFilterStartDateRange}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                <Search className="h-4 w-4 mr-2" />
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : filteredData ? (
        <PriceTableTable
          data={filteredData}
          currentPage={page}
          onPageChange={handlePageChange}
          canEdit={canEdit}
          canClone={canCreate}
        />
      ) : null}
    </motion.div>
  );
}
